class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();

    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    super.onFormSubmit(event);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;

    const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matches = currentButtonText.match(new RegExp(escapedPreviousTerm, 'g')) || [];

    if (matches.length !== 1) {
      // Avoid replacing when term is missing or appears multiple times in the button text.
      return;
    }

    const newButtonText = currentButtonText.replace(previousTerm, newTerm);
    searchForTextElement.innerText = newButtonText;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const normalizedSearchTerm = this.getNormalizedQuery(searchTerm);
    const queryKey = normalizedSearchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const renderAndCacheResults = (resultsMarkup) => {
      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
    };

    if (this.isSkuQuery(normalizedSearchTerm)) {
      Promise.allSettled([
        this.fetchSearchProductsMarkup(normalizedSearchTerm, signal),
        this.fetchSearchSuggestions(normalizedSearchTerm, signal),
      ])
        .then(([searchProductsResult, searchSuggestionsResult]) => {
          if (searchProductsResult.status !== 'fulfilled') {
            throw searchProductsResult.reason;
          }

          const resultsMarkup =
            searchSuggestionsResult.status === 'fulfilled'
              ? this.injectSearchSuggestionsMarkup(searchProductsResult.value, searchSuggestionsResult.value)
              : searchProductsResult.value;

          renderAndCacheResults(resultsMarkup);
        })
        .catch((error) => this.handleSearchError(error));
      return;
    }

    this.fetchAndCacheThemePredictiveResults(normalizedSearchTerm, queryKey, signal)
      .then((resultsMarkup) => {
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => this.handleSearchError(error));
  }

  async fetchAndCacheThemePredictiveResults(normalizedSearchTerm, queryKey, signal) {
    return fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(normalizedSearchTerm)}&section_id=predictive-search&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku,variants.barcode`,
      { signal }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        return resultsMarkup;
      });
  }

  async fetchSearchProductsMarkup(normalizedSearchTerm, signal) {
    const searchUrl = routes?.search_url || '/search';

    return fetch(
      `${searchUrl}?q=${encodeURIComponent(
        normalizedSearchTerm
      )}&section_id=search-sku-predictive-products&type=product&options[prefix]=last`,
      { signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const parsedMarkup = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = parsedMarkup.querySelector('#shopify-section-search-sku-predictive-products')?.innerHTML || '';

        if (!resultsMarkup) throw new Error('Missing search SKU predictive products markup');

        return this.limitSearchProductsMarkup(resultsMarkup, 5);
      });
  }

  async fetchSearchSuggestions(normalizedSearchTerm, signal) {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const queryParams = new URLSearchParams({
      q: normalizedSearchTerm,
      'resources[type]': 'query,collection',
    });

    return fetch(`${rootUrl}search/suggest.json?${queryParams.toString()}`, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((responseJson) => ({
        queries: responseJson?.resources?.results?.queries || [],
        collections: responseJson?.resources?.results?.collections || [],
      }));
  }

  injectSearchSuggestionsMarkup(resultsMarkup, suggestions) {
    const parsedMarkup = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const queryWrapper = parsedMarkup.querySelector('#predictive-search-results-queries');
    const queryList = parsedMarkup.querySelector('#predictive-search-results-queries-list');
    const resultsGroupsWrapper = parsedMarkup.querySelector('#predictive-search-results-groups-wrapper');
    const queries = suggestions?.queries || [];
    const collections = suggestions?.collections || [];
    const hasSuggestions = queries.length > 0 || collections.length > 0;

    if (!queryWrapper || !queryList || !resultsGroupsWrapper) return resultsMarkup;

    if (!hasSuggestions) {
      queryWrapper.remove();
    } else {
      queryList.innerHTML = '';

      queries.forEach((query, index) => {
        queryList.appendChild(this.buildSuggestionListItem(parsedMarkup, {
          id: `predictive-search-option-query-${index + 1}`,
          url: query?.url || `${routes?.search_url || '/search'}?q=${encodeURIComponent(query?.text || '')}`,
          headingClass: 'predictive-search__item-heading predictive-search__item-query-result h5',
          ariaLabel: query?.text || '',
          text: query?.text || '',
          html: query?.styled_text || query?.styledText || '',
        }));
      });

      collections.forEach((collection, index) => {
        queryList.appendChild(this.buildSuggestionListItem(parsedMarkup, {
          id: `predictive-search-option-collection-${index + 1}`,
          url: collection?.url || '',
          headingClass: 'predictive-search__item-heading h5',
          text: collection?.title || '',
        }));
      });
    }

    resultsGroupsWrapper.classList.toggle(
      'predictive-search__results-groups-wrapper--no-suggestions',
      !hasSuggestions
    );

    this.updateSkuSearchLiveRegionCount(parsedMarkup, queries.length + collections.length);

    return parsedMarkup.body.innerHTML;
  }

  limitSearchProductsMarkup(resultsMarkup, maxResults) {
    const parsedMarkup = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const productList = parsedMarkup.querySelector('#predictive-search-results-products-list');
    const liveRegionCount = parsedMarkup.querySelector('[data-predictive-search-live-region-count-value]');

    if (!productList) return resultsMarkup;

    const prioritizedItem = Array.from(productList.querySelectorAll('li')).find((productItem) =>
      /order\s*:\s*-1/i.test(productItem.getAttribute('style') || '')
    );

    if (prioritizedItem) {
      prioritizedItem.removeAttribute('style');
      productList.prepend(prioritizedItem);
    }

    productList.removeAttribute('style');

    const productItems = Array.from(productList.querySelectorAll('li'));
    productItems.slice(maxResults).forEach((productItem) => productItem.remove());

    Array.from(productList.querySelectorAll('li')).forEach((productItem, index) => {
      productItem.id = `predictive-search-option-product-${index + 1}`;
    });

    if (liveRegionCount) {
      const displayedProductsCount = productList.querySelectorAll('li').length;
      liveRegionCount.textContent = this.getSkuSearchLiveRegionCountText(liveRegionCount, displayedProductsCount, 0);
    }

    return parsedMarkup.body.innerHTML;
  }

  buildSuggestionListItem(documentRoot, suggestion) {
    const listItem = documentRoot.createElement('li');
    listItem.id = suggestion.id;
    listItem.className = 'predictive-search__list-item';
    listItem.setAttribute('role', 'option');
    listItem.setAttribute('aria-selected', 'false');

    const link = documentRoot.createElement('a');
    link.href = suggestion.url;
    link.className = 'predictive-search__item link link--text';
    link.tabIndex = -1;

    const content = documentRoot.createElement('div');
    content.className = 'predictive-search__item-content predictive-search__item-content--centered';

    const heading = documentRoot.createElement('p');
    heading.className = suggestion.headingClass;
    if (suggestion.ariaLabel) heading.setAttribute('aria-label', suggestion.ariaLabel);

    if (suggestion.html) {
      heading.innerHTML = suggestion.html;
    } else {
      heading.textContent = suggestion.text;
    }

    content.appendChild(heading);
    link.appendChild(content);
    listItem.appendChild(link);

    return listItem;
  }

  updateSkuSearchLiveRegionCount(documentRoot, suggestionsCount) {
    const liveRegionCount = documentRoot.querySelector('[data-predictive-search-live-region-count-value]');
    const productCount = documentRoot.querySelectorAll('#predictive-search-results-products-list > li').length;
    if (!liveRegionCount) return;

    liveRegionCount.textContent = this.getSkuSearchLiveRegionCountText(liveRegionCount, productCount, suggestionsCount);
  }

  getSkuSearchLiveRegionCountText(liveRegionCount, productCount, suggestionsCount) {
    const totalCount = productCount + suggestionsCount;
    if (!totalCount) {
      return (liveRegionCount.dataset.noResultsText || '').replace('[terms]', this.searchTerm || this.getQuery());
    }

    const countParts = [];
    if (suggestionsCount) {
      countParts.push(this.formatLiveRegionCountText(liveRegionCount, 'suggestionsCount', suggestionsCount));
    }
    if (productCount) {
      countParts.push(this.formatLiveRegionCountText(liveRegionCount, 'productsCount', productCount));
    }

    return `${this.formatLiveRegionCountText(liveRegionCount, 'resultsCount', totalCount)}: ${countParts.join(', ')}`;
  }

  formatLiveRegionCountText(liveRegionCount, key, count) {
    const templateKey = count === 1 ? `${key}One` : `${key}Other`;
    const template = liveRegionCount.dataset[templateKey] || '';
    return template.replace('[count]', count);
  }

  isSkuQuery(searchTerm) {
    if (!searchTerm || searchTerm.includes(' ')) return false;
    return searchTerm.length >= 6 && /^(SM-|CT-)[A-Z0-9-]+$/.test(searchTerm);
  }

  handleSearchError(error) {
    if (error?.name === 'AbortError' || error?.code === 20) {
      return;
    }
    this.close();
    throw error;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (!liveRegionCount) return;
    this.setLiveRegionText(liveRegionCount.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
